import { useEffect, useEffectEvent, useMemo, useState } from "react";
import { Box } from "@gemini/core";
import { Button, CellContent } from "@gemini/ui";
import { useAuthentication } from "@aviv-unified-frontend/runtime/authentication";
import { useBrand } from "@aviv-unified-frontend/runtime/brand";
import { useLocale } from "@aviv-unified-frontend/runtime/i18n";

import { logger } from "../../../utils/logger";
import { List } from "../List";

import { createTabPanel } from "./createTabPanel";

export const DebugTabPanel = createTabPanel({
  label: "Debug",
  description: "Debugging information and actions",
  Component() {
    const { id } = useBrand();
    const { language } = useLocale();

    const { authenticate, state, error, unsafe_accessToken } =
      useAuthentication();

    const [shouldHandleAfterAuth, setShouldHandleAfterAuth] = useState(false);

    const handleAfterAuth = useEffectEvent(() => {
      logger.debug("Debug Panel - post authenticate():", {
        state,
        error,
        unsafe_accessToken,
      });
    });

    useEffect(() => {
      if (shouldHandleAfterAuth) {
        handleAfterAuth();
        setShouldHandleAfterAuth(false);
      }
    }, [shouldHandleAfterAuth]);

    const actionItems = useMemo(
      () =>
        createActionItems({
          authenticate: async () => {
            await authenticate({ shouldRedirect: false });
            setShouldHandleAfterAuth(true);
          },
        }),
      [authenticate],
    );

    return (
      <>
        <List<{
          key: string;
          title: string;
        }>
          items={[
            {
              key: id,
              title: "Brand",
            },
            {
              key: language,
              title: "Language",
            },
          ]}
          renderItem={({ key, title }) => (
            <CellContent
              key={key}
              title={title}
              description={key}
            />
          )}
        />
        <List
          items={actionItems}
          renderItem={({ action, name, key, description }) => (
            <Box
              key={key}
              display="flex"
              flexDirection="column"
            >
              <Box
                as="p"
                typography="typography.body.12.regular"
              >
                {description}
              </Box>
              <Button
                size="32"
                type="button"
                onPress={action}
              >
                {name}
              </Button>
            </Box>
          )}
        />
      </>
    );
  },
});

const createActionItems = ({
  authenticate,
}: {
  authenticate: () => Promise<void>;
}) => {
  return [
    {
      id: "Authenticate",
      action: authenticate,
      name: "authenticate()",
      description: "Check a user's authentication status.",
      key: "Authenticate",
    },
    {
      id: "ThrowAnError",
      action: () => {
        throw new Error("This is a test error.");
      },
      name: "Throw an error",
      description: "Throw a test error.",
      key: "ThrowAnError",
    },
  ];
};
